import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  PersonAdd,
  ShoppingCart
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { apiClient } from '../services/api';
import PageHeader from '../components/ui/PageHeader';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

interface CustomerMetrics {
  totalCustomers: number;
  newCustomers: number;
  activeCustomers: number;
  averageOrderValue: number;
  retentionRate: number;
  growthRate: number;
}

interface CustomerDashboardData {
  metrics: CustomerMetrics;
  customerDistribution: Array<{ type: string; count: number; percentage: number }>;
  topCustomers: Array<any>;
  activityTrends: Array<any>;
  newCustomersChart: Array<any>;
  engagement: any;
}

const CustomerAnalyticsDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [dashboardData, setDashboardData] = useState<CustomerDashboardData | null>(null);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/admin/analytics/customers/dashboard`, {
        params: { period }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch customer analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [period]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-IN').format(value);
  };

  if (loading || !dashboardData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const { metrics, customerDistribution, topCustomers, activityTrends } = dashboardData;

  return (
    <Box>
      <PageHeader
        title="Customer Analytics"
        subtitle="Comprehensive customer insights and behavior analysis"
      />

      {/* Period Selector */}
      <Box mb={3}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Time Period</InputLabel>
          <Select
            value={period}
            label="Time Period"
            onChange={(e) => setPeriod(e.target.value)}
          >
            <MenuItem value="7d">Last 7 Days</MenuItem>
            <MenuItem value="30d">Last 30 Days</MenuItem>
            <MenuItem value="90d">Last 90 Days</MenuItem>
            <MenuItem value="1y">Last Year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total Customers
                  </Typography>
                  <Typography variant="h4">{formatNumber(metrics.totalCustomers)}</Typography>
                </Box>
                <People sx={{ fontSize: 48, color: '#0088FE', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    New Customers
                  </Typography>
                  <Typography variant="h4">{formatNumber(metrics.newCustomers)}</Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    {metrics.growthRate >= 0 ? (
                      <TrendingUp sx={{ color: 'success.main', mr: 0.5 }} />
                    ) : (
                      <TrendingDown sx={{ color: 'error.main', mr: 0.5 }} />
                    )}
                    <Typography
                      variant="body2"
                      color={metrics.growthRate >= 0 ? 'success.main' : 'error.main'}
                    >
                      {Math.abs(metrics.growthRate).toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
                <PersonAdd sx={{ fontSize: 48, color: '#00C49F', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Active Customers
                  </Typography>
                  <Typography variant="h4">{formatNumber(metrics.activeCustomers)}</Typography>
                  <Typography variant="body2" color="textSecondary" mt={1}>
                    Retention: {metrics.retentionRate.toFixed(1)}%
                  </Typography>
                </Box>
                <ShoppingCart sx={{ fontSize: 48, color: '#FFBB28', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} mb={3}>
        {/* Customer Activity Trends */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer Activity Trends
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={activityTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="activeCustomers" stroke="#0088FE" name="Active Customers" />
                  <Line type="monotone" dataKey="newCustomers" stroke="#00C49F" name="New Customers" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Customer Type Distribution */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={customerDistribution}
                    dataKey="count"
                    nameKey="type"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {customerDistribution.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Customers Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Top Customers by Revenue
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#0d47a1' }}>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Orders</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Revenue</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Avg Order Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topCustomers.map((customer) => (
                  <TableRow key={customer.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {customer.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {customer.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={customer.customerType} size="small" />
                    </TableCell>
                    <TableCell align="right">{customer.totalOrders}</TableCell>
                    <TableCell align="right">{formatCurrency(customer.totalRevenue)}</TableCell>
                    <TableCell align="right">{formatCurrency(customer.averageOrderValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CustomerAnalyticsDashboard;
